/**
 * Enrollment, the roster of who has joined: the dashboard's fifth screen (Sep 17 2026).
 *
 * The one place a student appears as a person on this surface. Thomas's
 * pivot of Sep 17 2026: the institution's own administrators already manage
 * these students, so this screen lists WHO joined, by first name and last
 * initial, when, and whether each has a mentor, and the mentors affiliated
 * with the institution. What a student DOES in P3 is not here and never
 * will be for one person: every activity figure on the dashboard covers ten
 * or more students, and that is what the join screen and the Addendum say.
 *
 * Admin and owner only (the Worker answers a viewer 403 and the rail hides
 * the item). A historic institution, one on P3 before rosters began, sees
 * counts only and a sentence saying why. /demo renders a sample.
 *
 * ── COHORTS (Sep 17 2026, Thomas's second ask) ───────────────────────────
 * The same screen carries the institution's own cohorts: a card to make,
 * rename and archive them, a dropdown on every student's row to place them
 * in one, and a filter on the list. A cohort is a label the institution
 * keeps in P3 Enterprise and nowhere else (Worker lib/cohorts.ts): the
 * platform never sees it, no student or mentor does, and the membership is
 * the platform id already on this list. The floor of ten applies to every
 * cohort on its own, and the card says so beside each count.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AdminShell,
  ExportPdfButton,
  type PaletteCommand,
} from "~/app/components/AdminShell";
import { LiveGate, useLiveTenant } from "~/app/components/LiveTenant";
import {
  Button,
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  Chip,
  DataTable,
  Field,
  Input,
  SectionHead,
  Select,
  StateCard,
} from "~/app/components/primitives";
import { ADMIN_SECTIONS } from "~/app/lib/admin-nav";
import {
  assignCohort,
  clearCohort,
  createCohort,
  getCohorts,
  getRoster,
  updateCohort,
  type CohortKind,
  type CohortRecord,
  type CohortsPayload,
  type RosterPayload,
} from "~/app/lib/api";
import {
  DEFAULT_FILTERS,
  DEMO_COHORTS,
  demoClaimedCount,
  demoCohortSizes,
  deriveCohort,
  scenarioFromSearch,
} from "~/app/lib/demo-data";
import { formatCount, formatDate, pluralize } from "~/app/lib/format";
import { liveView } from "~/app/lib/live-view";
import { viewerOf, type SessionPayload } from "~/app/lib/session";
import { K } from "~/lib/suppression";

/** The sentence under both tables. Every clause is a rule the Worker keeps. */
export const ROSTER_NOTE =
  "Names are shown as a first name and last initial, the way a mentor sees a student in the app. What a student does in P3 is never shown for one person: every activity figure on this dashboard covers ten or more students.";

/** The historic case: an institution on P3 before rosters began. */
export const ROSTER_HISTORIC = {
  title: "Your institution joined before rosters began",
  body: "Enrollment is available to institutions that registered from September 17, 2026, when students began joining under the wording that allows it. Your figures are unaffected.",
} as const;

export const ROSTER_ROLE_NOTE = {
  title: "This screen is for owners and admins",
  body: "Ask an owner or an admin at your institution to open it, or to change your role.",
} as const;

/** Under the cohorts card. Each clause is a rule lib/cohorts.ts or the floor keeps. */
export const COHORT_NOTE = `Cohorts are your own labels for the students on this list. P3 never shows them to a student or a mentor, and no figure is published for a cohort under ${K} students.`;

export const COHORT_KIND_LABEL: Record<CohortKind, string> = {
  department: "Department",
  semester: "Semester",
  program: "Program",
  other: "Other",
};

type RosterState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly roster: RosterPayload }
  | { readonly kind: "forbidden" }
  | { readonly kind: "down"; readonly message: string };

type CohortsState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly cohorts: CohortsPayload }
  | { readonly kind: "down"; readonly message: string };

/** A sample roster for /demo: fixed names, fixed shape, no real person. */
export function sampleRoster(now: number): RosterPayload {
  const day = 86_400;
  const students = [
    ["Amara O.", 62, "matched"],
    ["Ben S.", 60, "matched"],
    ["Carla M.", 58, "waiting"],
    ["Devon R.", 55, "matched"],
    ["Elena T.", 51, "none"],
    ["Farid A.", 49, "matched"],
    ["Grace L.", 44, "matched"],
    ["Hector V.", 41, "waiting"],
    ["Imani W.", 37, "matched"],
    ["Jonah K.", 33, "none"],
    ["Keisha B.", 30, "matched"],
    ["Luis F.", 26, "matched"],
    ["Maya P.", 21, "waiting"],
    ["Noah C.", 18, "matched"],
    ["Olivia D.", 12, "none"],
    ["Priya N.", 9, "matched"],
    ["Quentin H.", 5, "none"],
    ["Rosa E.", 2, "waiting"],
  ] as const;
  const mentors = [
    ["Angela M.", "Director of Career Services", 3, 5],
    ["Marcus T.", "Alumni Relations Manager", 5, 5],
    ["Simone K.", "Assistant Professor of Nursing", 1, 3],
  ] as const;
  const rows = students.map(([name, ago, mentor], i) => ({
    id: `sample-s${i}`,
    name,
    joinedAt: now - ago * day,
    mentor,
    mentorships: mentor === "matched" ? 1 : 0,
  }));
  return {
    available: true,
    since: now - 70 * day,
    refreshedAt: now - 1_800,
    students: rows,
    mentors: mentors.map(([name, title, held, capacity], i) => ({
      id: `sample-m${i}`,
      name,
      title,
      status: "APPROVED",
      joinedAt: now - (80 - i * 9) * day,
      mentorships: held,
      capacity,
    })),
    counts: {
      students: rows.length,
      matched: rows.filter((r) => r.mentor === "matched").length,
      waiting: rows.filter((r) => r.mentor === "waiting").length,
      mentors: mentors.length,
    },
  };
}

/**
 * Sample cohorts for /demo: two over the floor and one under it, so the card
 * shows both states. Memberships point at the sample students listed. With
 * `sizes` (Sep 19 2026), each cohort's count is the sample dashboard's own
 * (`demoCohortSizes`), because the list is eighteen of the institution's
 * students and the dashboard cuts its cohorts from all of them.
 */
export function sampleCohorts(
  now: number,
  roster: RosterPayload,
  sizes?: Readonly<Record<string, number>>,
): CohortsPayload {
  const day = 86_400;
  // The same cohorts the sample dashboard's filter offers (DEMO_COHORTS).
  const [nursing, orientation, honors] = [DEMO_COHORTS[0]!, DEMO_COHORTS[1]!, DEMO_COHORTS[2]!];
  const memberships: Record<string, string> = {};
  // The last student listed is in no cohort, so the filter's "None" has one.
  roster.students.forEach((s, i) => {
    if (i < 6) memberships[s.id] = nursing.id;
    else if (i < 16) memberships[s.id] = orientation.id;
    else if (i === 16) memberships[s.id] = honors.id;
  });
  const count = (id: string) => sizes?.[id] ?? Object.values(memberships).filter((c) => c === id).length;
  return {
    available: true,
    cohorts: [
      { id: nursing.id, name: nursing.name, kind: "department", createdAt: now - 40 * day, archivedAt: null, members: count(nursing.id) },
      { id: orientation.id, name: orientation.name, kind: "semester", createdAt: now - 30 * day, archivedAt: null, members: count(orientation.id) },
      { id: honors.id, name: honors.name, kind: "program", createdAt: now - 12 * day, archivedAt: null, members: count(honors.id) },
    ],
    memberships,
    limit: 25,
    kinds: ["department", "semester", "program", "other"],
  };
}

function mentorChip(mentor: RosterPayload["students"][number]["mentor"]) {
  if (mentor === "matched") return <Chip tone="good">Has a mentor</Chip>;
  if (mentor === "waiting") return <Chip tone="warn">Waiting for a mentor</Chip>;
  return <Chip tone="neutral">Not yet</Chip>;
}

function mentorText(mentor: RosterPayload["students"][number]["mentor"]): string {
  return mentor === "matched" ? "Has a mentor" : mentor === "waiting" ? "Waiting for a mentor" : "Not yet";
}

/** The platform's status words, in the institution's language. */
export function mentorStatusLabel(status: string | null): string {
  switch ((status ?? "").toUpperCase()) {
    case "APPROVED":
      return "Approved";
    case "PENDING":
      return "Awaiting P3 approval";
    case "DECLINED":
      return "Not approved";
    case "SUSPENDED":
      return "Suspended";
    case "CANCELLED":
    case "CANCELED":
      return "Left P3";
    default:
      return status ?? "Unknown";
  }
}

/** How many of each cohort's members a payload's memberships list. */
function tallyOf(payload: CohortsPayload): Map<string, number> {
  const tally = new Map<string, number>();
  for (const cohortId of Object.values(payload.memberships)) tally.set(cohortId, (tally.get(cohortId) ?? 0) + 1);
  return tally;
}

/**
 * The cohorts' members recounted from the memberships, for the sample host's
 * local edits. `offscreen` is each cohort's members beyond the students the
 * sample lists (Sep 19 2026), so an edit moves the dashboard's count by one.
 */
function recount(payload: CohortsPayload, offscreen: Readonly<Record<string, number>> = {}): CohortsPayload {
  const tally = tallyOf(payload);
  return { ...payload, cohorts: payload.cohorts.map((c) => ({ ...c, members: (offscreen[c.id] ?? 0) + (tally.get(c.id) ?? 0) })) };
}

/** Each cohort's members that the sample's list does not show. */
function offscreenOf(payload: CohortsPayload): Record<string, number> {
  const tally = tallyOf(payload);
  return Object.fromEntries(payload.cohorts.map((c) => [c.id, Math.max(0, c.members - (tally.get(c.id) ?? 0))]));
}

export function AdminRoster({
  basePath,
  demo = false,
  session,
}: {
  basePath: "/admin" | "/demo";
  demo?: boolean;
  session?: SessionPayload;
}) {
  const live = useLiveTenant(session);
  const scenario = useMemo(
    () => scenarioFromSearch(typeof window === "undefined" ? "" : window.location.search),
    [],
  );
  const sampleView = useMemo(() => deriveCohort(scenario, DEFAULT_FILTERS), [scenario]);
  const data = live.state.kind === "ready" ? live.state.data : null;
  const view = useMemo(() => (data ? liveView(data) : sampleView), [data, sampleView]);
  const isLive = data !== null && session !== undefined;
  /** The sample dashboard's claimed count, which its list is a part of. */
  const sampleClaimed = useMemo(() => demoClaimedCount(scenario), [scenario]);

  const [roster, setRoster] = useState<RosterState>(() => ({ kind: "loading" }));
  const [cohorts, setCohorts] = useState<CohortsState>(() => ({ kind: "loading" }));
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState("");
  /** "" is every student, "none" the ones in no cohort, else a cohort id. */
  const [cohortFilter, setCohortFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!session) {
      const sample = sampleRoster(Math.floor(Date.now() / 1000));
      setRoster({ kind: "ready", roster: sample });
      setCohorts({ kind: "ready", cohorts: sampleCohorts(Math.floor(Date.now() / 1000), sample, demoCohortSizes(scenario)) });
      return;
    }
    let cancelled = false;
    setRoster({ kind: "loading" });
    setCohorts({ kind: "loading" });
    void getRoster().then((result) => {
      if (cancelled) return;
      if (result.ok) setRoster({ kind: "ready", roster: result.value });
      else if (result.status === 403) setRoster({ kind: "forbidden" });
      else setRoster({ kind: "down", message: result.error.message });
    });
    void getCohorts().then((result) => {
      if (cancelled) return;
      if (result.ok) setCohorts({ kind: "ready", cohorts: result.value });
      else setCohorts({ kind: "down", message: result.error.message });
    });
    return () => {
      cancelled = true;
    };
  }, [session, attempt, scenario]);

  /** After a write on a live tenant: the Worker's list again, counts included. */
  const reloadCohorts = useCallback(async () => {
    const result = await getCohorts();
    if (result.ok) setCohorts({ kind: "ready", cohorts: result.value });
    else setNotice(result.error.message);
  }, []);

  /** One write, then the list again; the Worker's own sentence on a refusal. */
  const write = useCallback(
    async (
      call: () => Promise<{ ok: true } | { ok: false; error: { message: string } }>,
      local: (current: CohortsPayload) => CohortsPayload,
    ): Promise<boolean> => {
      if (!isLive) {
        setCohorts((current) =>
          current.kind === "ready"
            ? { kind: "ready", cohorts: recount(local(current.cohorts), offscreenOf(current.cohorts)) }
            : current,
        );
        setNotice(null);
        return true;
      }
      setBusy(true);
      setNotice(null);
      const result = await call();
      if (result.ok) await reloadCohorts();
      else setNotice(result.error.message);
      setBusy(false);
      return result.ok;
    },
    [isLive, reloadCohorts],
  );

  if (session && live.state.kind !== "ready") {
    return <LiveGate state={live.state} reload={live.reload} />;
  }

  const { institution, seat } = view;
  const commands: PaletteCommand[] = [
    {
      id: "print",
      group: "Share",
      label: "Export as PDF, or print",
      keywords: "board pdf paper export print",
      run: () => window.print(),
    },
  ];

  const needle = query.trim().toLowerCase();
  const matches = (name: string, extra = "") =>
    needle === "" || `${name} ${extra}`.toLowerCase().includes(needle);

  const cohortActions: CohortActions = {
    busy,
    notice,
    create: (name, kind) =>
      write(
        () => createCohort(name, kind),
        (current) => ({
          ...current,
          cohorts: [
            ...current.cohorts,
            { id: `sample-c${Date.now()}`, name, kind, createdAt: Math.floor(Date.now() / 1000), archivedAt: null, members: 0 },
          ],
        }),
      ),
    rename: (id, name) =>
      write(
        () => updateCohort(id, { name }),
        (current) => ({ ...current, cohorts: current.cohorts.map((c) => (c.id === id ? { ...c, name } : c)) }),
      ),
    setArchived: (id, archived) =>
      write(
        () => updateCohort(id, { archived }),
        (current) => ({
          ...current,
          cohorts: current.cohorts.map((c) =>
            c.id === id ? { ...c, archivedAt: archived ? Math.floor(Date.now() / 1000) : null } : c,
          ),
        }),
      ),
    place: (studentId, cohortId, currentCohortId) =>
      write(
        () => (cohortId === "" ? clearCohort(currentCohortId ?? "", studentId) : assignCohort(cohortId, studentId)),
        (current) => {
          const memberships = { ...current.memberships };
          if (cohortId === "") delete memberships[studentId];
          else memberships[studentId] = cohortId;
          return { ...current, memberships };
        },
      ),
    retry: () => {
      setCohorts({ kind: "loading" });
      void reloadCohorts();
    },
  };

  return (
    <AdminShell
      institution={institution}
      viewer={viewerOf(session)}
      basePath={basePath}
      page="enrollment"
      isSample={!isLive}
      sections={[...ADMIN_SECTIONS]}
      seat={seat}
      headerActions={<ExportPdfButton />}
      commands={commands}
    >
      <section className="e-sec" style={{ marginTop: 0 }}>
        <SectionHead
          level="page"
          id="enrollment"
          title="Enrollment"
          lede="Each student and mentor on your institution, by first name and last initial. Refreshed hourly."
        />


        {roster.kind === "loading" ? (
          <StateCard variant="loading">Fetching enrollment.</StateCard>
        ) : roster.kind === "forbidden" ? (
          <StateCard variant="empty" title={ROSTER_ROLE_NOTE.title}>
            {ROSTER_ROLE_NOTE.body}
          </StateCard>
        ) : roster.kind === "down" ? (
          <StateCard
            variant="down"
            title="We could not load enrollment"
            action={
              <Button variant="secondary" size="sm" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </Button>
            }
          >
            {roster.message} The rest of the dashboard is unaffected.
          </StateCard>
        ) : !roster.roster.available ? (
          <StateCard variant="empty" title={ROSTER_HISTORIC.title}>
            {ROSTER_HISTORIC.body}
          </StateCard>
        ) : (
          <RosterTables
            roster={roster.roster}
            cohorts={cohorts}
            actions={cohortActions}
            query={query}
            onQuery={setQuery}
            cohortFilter={cohortFilter}
            onCohortFilter={setCohortFilter}
            matches={matches}
            listedNote={
              // The sample lists eighteen of its students (Sep 19 2026), and
              // says so, so its counts and the dashboard's can be read together.
              !isLive && sampleClaimed > roster.roster.students.length
                ? `The sample lists ${formatCount(roster.roster.students.length)} of its ${formatCount(sampleClaimed)} students.`
                : null
            }
          />
        )}
      </section>
    </AdminShell>
  );
}

interface CohortActions {
  readonly busy: boolean;
  readonly notice: string | null;
  readonly create: (name: string, kind: CohortKind) => Promise<boolean>;
  readonly rename: (id: string, name: string) => Promise<boolean>;
  readonly setArchived: (id: string, archived: boolean) => Promise<boolean>;
  /** cohortId "" takes the student out of `currentCohortId`. */
  readonly place: (studentId: string, cohortId: string, currentCohortId: string | null) => Promise<boolean>;
  readonly retry: () => void;
}

function RosterTables({
  roster,
  cohorts,
  actions,
  query,
  onQuery,
  cohortFilter,
  onCohortFilter,
  matches,
  listedNote = null,
}: {
  roster: RosterPayload;
  cohorts: CohortsState;
  actions: CohortActions;
  query: string;
  onQuery: (value: string) => void;
  cohortFilter: string;
  onCohortFilter: (value: string) => void;
  matches: (name: string, extra?: string) => boolean;
  /** On the sample only: that the list is part of the institution. */
  listedNote?: string | null;
}) {
  const payload = cohorts.kind === "ready" ? cohorts.cohorts : null;
  const active = payload ? payload.cohorts.filter((c) => c.archivedAt === null) : [];
  const memberships = payload?.memberships ?? {};
  const nameOf = (cohortId: string | undefined): string =>
    (cohortId && payload?.cohorts.find((c) => c.id === cohortId)?.name) || "None";
  const inFilter = (studentId: string): boolean =>
    cohortFilter === "" ||
    (cohortFilter === "none" ? memberships[studentId] === undefined : memberships[studentId] === cohortFilter);

  const students = roster.students.filter((s) => matches(s.name) && inFilter(s.id));
  const filtered = query.trim() !== "" || cohortFilter !== "";

  // Thomas, Sep 18 2026: no card above Cohorts. The search and the cohort
  // filter act on students only, so they sit in the Students card; the
  // freshness line moves to its footnote.
  return (
    <div className="e-grid">
      <CohortsCard cohorts={cohorts} actions={actions} />

      <Card variant="table" span="full">
        <CardHeader>
          <CardTitle>Students</CardTitle>
        </CardHeader>
        <CardBody>
          {/* No visible labels (Thomas, Sep 18 2026): the placeholder and the
              first option say what each control is, so the names are for a
              screen reader only. */}
          <div className="e-roster-filters is-in-card">
            <Input
              type="search"
              aria-label="Find a student"
              value={query}
              placeholder="Start typing a first name"
              onChange={(event) => onQuery(event.target.value)}
              autoComplete="off"
            />
            {active.length > 0 ? (
              <Select
                aria-label="Filter students by cohort"
                value={cohortFilter}
                onChange={(event) => onCohortFilter(event.target.value)}
              >
                <option value="">All students</option>
                <option value="none">No cohort</option>
                {active.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            ) : null}
          </div>
          {students.length === 0 ? (
            <StateCard variant="empty" title={filtered ? "No student matches" : "No student has joined yet"}>
              {filtered
                ? "Try fewer letters, or clear the search and the cohort."
                : "Students appear here the hour after they join from your link, code or poster."}
            </StateCard>
          ) : (
            <DataTable
              caption="Students who have joined this institution"
              rows={students}
              rowKey={(s) => s.id}
              cap={2000}
              moreNoun="student"
              columns={[
                { key: "name", header: "Student", cell: (s) => <span className="e-ui">{s.name}</span>, text: (s) => s.name, align: "left" },
                {
                  key: "joined",
                  header: "Joined",
                  cell: (s) => <span style={{ whiteSpace: "nowrap" }}>{s.joinedAt ? formatDate(s.joinedAt * 1000) : "Unknown"}</span>,
                  text: (s) => (s.joinedAt ? formatDate(s.joinedAt * 1000) : "Unknown"),
                  align: "left",
                },
                { key: "mentor", header: "Mentor", cell: (s) => mentorChip(s.mentor), text: (s) => mentorText(s.mentor), align: "left" },
                ...(active.length > 0
                  ? [
                      {
                        key: "cohort",
                        header: "Cohort",
                        cell: (s: RosterPayload["students"][number]) => (
                          <Select
                            className="e-cell-select"
                            aria-label={`Cohort for ${s.name}`}
                            value={memberships[s.id] ?? ""}
                            disabled={actions.busy}
                            onChange={(event) => void actions.place(s.id, event.target.value, memberships[s.id] ?? null)}
                          >
                            <option value="">None</option>
                            {active.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </Select>
                        ),
                        text: (s: RosterPayload["students"][number]) => nameOf(memberships[s.id]),
                        align: "left" as const,
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </CardBody>
        <CardFootnote>
          <p className="e-note">
            {roster.refreshedAt ? `As of ${formatDate(roster.refreshedAt * 1000)}.` : "No refresh has run yet."}
            {roster.since ? ` Roster since ${formatDate(roster.since * 1000)}.` : ""} {ROSTER_NOTE}
            {listedNote ? ` ${listedNote}` : ""}
          </p>
        </CardFootnote>
      </Card>

      <Card variant="table" span="full">
        <CardHeader>
          <CardTitle>Mentors</CardTitle>
        </CardHeader>
        <CardBody>
          {roster.mentors.length === 0 ? (
            <StateCard variant="empty" title="No mentor is affiliated yet">
              A mentor is listed here when they name your institution on their P3 profile.
            </StateCard>
          ) : (
            <DataTable
              caption="Mentors affiliated with this institution"
              rows={roster.mentors}
              rowKey={(m) => m.id}
              cap={500}
              moreNoun="mentor"
              columns={[
                { key: "name", header: "Mentor", cell: (m) => <span className="e-ui">{m.name}</span>, text: (m) => m.name, align: "left" },
                { key: "title", header: "Title", cell: (m) => m.title ?? "", text: (m) => m.title ?? "", align: "left", secondary: true },
                {
                  key: "status",
                  header: "Status",
                  cell: (m) =>
                    (m.status ?? "").toUpperCase() === "APPROVED" ? (
                      <Chip tone="good">Approved</Chip>
                    ) : (m.status ?? "").toUpperCase() === "PENDING" ? (
                      <Chip tone="warn">{mentorStatusLabel(m.status)}</Chip>
                    ) : (
                      <Chip tone="neutral">{mentorStatusLabel(m.status)}</Chip>
                    ),
                  text: (m) => mentorStatusLabel(m.status),
                  align: "left",
                },
                {
                  key: "mentoring",
                  header: "Mentoring",
                  cell: (m) => (m.capacity === null ? formatCount(m.mentorships) : `${formatCount(m.mentorships)} of ${formatCount(m.capacity)}`),
                  text: (m) => (m.capacity === null ? formatCount(m.mentorships) : `${formatCount(m.mentorships)} of ${formatCount(m.capacity)}`),
                },
              ]}
            />
          )}
        </CardBody>
        <CardFootnote>
          <p className="e-note">A mentor's count is the students they mentor across P3, not only yours.</p>
        </CardFootnote>
      </Card>
    </div>
  );
}

/**
 * The cohorts card: the list with rename and archive, and the form for a
 * new one. The floor is said beside every count, so a department of eight
 * reads "names only" here before anyone looks for its figures.
 */
function CohortsCard({ cohorts, actions }: { cohorts: CohortsState; actions: CohortActions }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<CohortKind>("department");
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  if (cohorts.kind === "loading") {
    return (
      <Card span="full">
        <CardHeader>
          <CardTitle>Cohorts</CardTitle>
        </CardHeader>
        <CardBody>
          <StateCard variant="loading">Fetching cohorts.</StateCard>
        </CardBody>
      </Card>
    );
  }
  if (cohorts.kind === "down") {
    return (
      <Card span="full">
        <CardHeader>
          <CardTitle>Cohorts</CardTitle>
        </CardHeader>
        <CardBody>
          <StateCard
            variant="down"
            title="We could not load your cohorts"
            action={
              <Button variant="secondary" size="sm" onClick={actions.retry}>
                Try again
              </Button>
            }
          >
            {cohorts.message} The rest of the list is unaffected.
          </StateCard>
        </CardBody>
      </Card>
    );
  }

  const payload = cohorts.cohorts;
  const archived = payload.cohorts.filter((c) => c.archivedAt !== null);
  const rows = showArchived ? payload.cohorts : payload.cohorts.filter((c) => c.archivedAt === null);
  const atLimit = payload.cohorts.length - archived.length >= payload.limit;

  const submit = async () => {
    const trimmed = name.trim().replace(/\s+/g, " ");
    if (trimmed === "") {
      setError("Give the cohort a name.");
      return;
    }
    if (trimmed.length > 60) {
      setError("A cohort's name is 60 characters at most.");
      return;
    }
    if (payload.cohorts.some((c) => c.archivedAt === null && c.name.toLowerCase() === trimmed.toLowerCase())) {
      setError(`A cohort named ${trimmed} already exists. Choose another name.`);
      return;
    }
    setError(null);
    if (await actions.create(trimmed, kind)) setName("");
  };

  const saveRename = async () => {
    if (!renaming) return;
    const trimmed = renaming.value.trim().replace(/\s+/g, " ");
    if (trimmed === "") return;
    if (await actions.rename(renaming.id, trimmed)) setRenaming(null);
  };

  /* The floor, said beside every count. Only the floor: what a cohort of ten
     unlocks is said where it is unlocked. */
  const floorChip = (c: CohortRecord) =>
    c.members >= K ? <Chip tone="good">{K} or more</Chip> : <Chip tone="neutral">Under {K}</Chip>;

  return (
    <Card variant="table" span="full">
      <CardHeader>
        <CardTitle sub="Your own groups of these students: a department, a semester, a program.">Cohorts</CardTitle>
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <StateCard variant="empty" title="No cohorts yet">
            Add one below, then set each student's cohort in the table.
          </StateCard>
        ) : (
          <DataTable
            caption="Cohorts of this institution's students"
            rows={rows}
            rowKey={(c) => c.id}
            cap={50}
            moreNoun="cohort"
            columns={[
              {
                key: "name",
                header: "Cohort",
                cell: (c) =>
                  renaming?.id === c.id ? (
                    <form
                      className="e-row"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void saveRename();
                      }}
                    >
                      <Input
                        className="e-cell-input"
                        aria-label={`New name for ${c.name}`}
                        value={renaming.value}
                        maxLength={60}
                        autoFocus
                        onChange={(event) => setRenaming({ id: c.id, value: event.target.value })}
                      />
                      <Button type="submit" variant="primary" size="sm" disabled={actions.busy}>
                        Save
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setRenaming(null)}>
                        Cancel
                      </Button>
                    </form>
                  ) : (
                    <span className="e-ui">
                      {c.name}
                      {c.archivedAt !== null ? <span className="e-note"> (archived)</span> : null}
                    </span>
                  ),
                text: (c) => (c.archivedAt !== null ? `${c.name} (archived)` : c.name),
                align: "left",
              },
              { key: "kind", header: "Kind", cell: (c) => COHORT_KIND_LABEL[c.kind], text: (c) => COHORT_KIND_LABEL[c.kind], align: "left", secondary: true },
              {
                key: "members",
                header: "Students",
                cell: (c) => (
                  <span className="e-row">
                    <span className="e-num">{formatCount(c.members)}</span>
                    {floorChip(c)}
                  </span>
                ),
                text: (c) => `${formatCount(c.members)} ${pluralize(c.members, "student")}`,
                align: "left",
              },
              {
                key: "actions",
                header: "",
                cell: (c) => (
                  <span className="e-row" style={{ justifyContent: "flex-end" }}>
                    {c.archivedAt === null ? (
                      <>
                        <Button variant="ghost" size="sm" disabled={actions.busy} onClick={() => setRenaming({ id: c.id, value: c.name })}>
                          Rename
                        </Button>
                        <Button variant="ghost" size="sm" disabled={actions.busy} onClick={() => void actions.setArchived(c.id, true)}>
                          Archive
                        </Button>
                      </>
                    ) : (
                      <Button variant="ghost" size="sm" disabled={actions.busy || atLimit} onClick={() => void actions.setArchived(c.id, false)}>
                        Restore
                      </Button>
                    )}
                  </span>
                ),
                text: () => "",
                align: "right",
              },
            ]}
          />
        )}

        {archived.length > 0 ? (
          <p className="e-note" style={{ marginTop: "var(--sp-3)" }}>
            <Button variant="ghost" size="sm" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? "Hide archived" : `Show archived (${formatCount(archived.length)})`}
            </Button>
          </p>
        ) : null}

        <form
          className="e-roster-new"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Field label="New cohort" error={error ?? undefined}>
            <Input
              value={name}
              placeholder="College of Nursing"
              maxLength={60}
              autoComplete="off"
              disabled={atLimit}
              onChange={(event) => {
                setName(event.target.value);
                if (error) setError(null);
              }}
            />
          </Field>
          <Field label="Kind">
            <Select value={kind} disabled={atLimit} onChange={(event) => setKind(event.target.value as CohortKind)}>
              {payload.kinds.map((k) => (
                <option key={k} value={k}>
                  {COHORT_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </Field>
          <div>
            <Button type="submit" variant="primary" size="sm" disabled={actions.busy || atLimit}>
              Add cohort
            </Button>
          </div>
        </form>
        {atLimit ? (
          <p className="e-note" role="status">
            Your institution has {formatCount(payload.limit)} cohorts, the most P3 keeps. Archive one to add another.
          </p>
        ) : null}
        {actions.notice ? (
          <p className="e-note" role="status" style={{ marginTop: "var(--sp-3)" }}>
            {actions.notice}
          </p>
        ) : null}
      </CardBody>
      <CardFootnote>
        <p className="e-note">{COHORT_NOTE}</p>
      </CardFootnote>
    </Card>
  );
}
